import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  setDocData,
  getDocDataSnap,
} from "../../../utils/firebase/Firebase.utils";
import toast from "react-hot-toast";
import SelectInput from "../Components/SelectInput";
import CommentBlock from "../../../components/CommentBlock";
import TaskAssignmentModule from "../../../components/TaskAssignmentModule";
import HistoryModule from "../../../components/HistoryModule";
import CommentsSection from "../../../components/CommentsSection";
import { FiCalendar, FiClock, FiUser, FiHash, FiEye } from "react-icons/fi";
import { dualNotification } from "../../../utils/EleghoUtils";
import LoadingSpinner from "../../../components/LoadingSpinner";
import { useCompany } from "../../../context/CompanyContext";

export default function FacilityReqPage({ data, setViewMOde, onStatusUpdate }) {
  const { getCapitalizedTerm } = useCompany();
  const [newStatus, setNewStatus] = useState(data.status || "");
  const [newPaymentStatus, setNewPaymentStatus] = useState(
    data.paymentStatus || "pending"
  );
  const [formData, setFormData] = useState(data);
  const [updateHappened, setUpdateHappened] = useState(false);
  const [paymentUpdateHappened, setPaymentUpdateHappened] = useState(false);
  const [comments, setComments] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [assignedToArray, setAssignedToArray] = useState(data.assignedTo || []);
  const currentUser = useSelector((state) => state.userObject);

  // Add loading state variables
  const [isStatusUpdating, setIsStatusUpdating] = useState(false);
  const [isPaymentStatusUpdating, setIsPaymentStatusUpdating] = useState(false);
  const [isAssignmentUpdating, setIsAssignmentUpdating] = useState(false);
  const [isCommentSubmitting, setIsCommentSubmitting] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      await getDocDataSnap("bookings", data.id, (data) => {
        setFormData(data);
        setComments(data.comments || []);
      });
    };
    fetchData();
  }, [data.id]);

  const formatDate = (timestamp) => {
    if (!timestamp) return "";
    if (timestamp.seconds) {
      const date = new Date(timestamp.seconds * 1000);
      return date.toLocaleString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    }
    return timestamp;
  };

  const status = [
    "pending",
    "reviewing",
    "approved",
    "rejected",
    "completed",
    "deposit refunded",
    "no refund necessary",
  ];
  const statusOptions = status.map((item) => ({ value: item, label: item }));

  const paymentStatusOptions = [
    { value: "pending", label: "Pending" },
    { value: "paid", label: "Paid" },
    { value: "partial", label: "Partial" },
    { value: "refunded", label: "Refunded" },
  ];

  const handleSelect = (selected) => {
    setUpdateHappened(true);
    setFormData({ ...formData, status: selected.value });
  };

  const handlePaymentStatusSelect = (selected) => {
    setPaymentUpdateHappened(true);
    setFormData({ ...formData, paymentStatus: selected.value });
  };

  const handleUpdateStatus = async () => {
    try {
      setIsStatusUpdating(true);
      const updatedStatus = formData.status;
      setNewStatus(updatedStatus);

      // Create history entry for status update
      const historyEntry = {
        action: `Status updated`,
        at: new Date().toLocaleString(),
        by: currentUser.displayName,
        to: updatedStatus,
      };

      const docObject = {
        ...formData,
        updatedBy: currentUser.uid,
        updatedName: currentUser.displayName,
        updatedEmail: currentUser.email,
        updateType: "Status is updated to " + updatedStatus,
        history: [...(formData?.history || []), historyEntry],
      };

      // Add approval details if status is approved
      if (updatedStatus === "approved") {
        docObject.approvedAt = new Date();
        docObject.approvedBy = currentUser.email;
        docObject.approvedByName = currentUser.displayName;
      }

      // Add completed details if status is completed
      if (updatedStatus === "completed") {
        docObject.completedAt = new Date();
        docObject.completedBy = currentUser.email;
        docObject.completedByName = currentUser.displayName;
      }

      // Add refund details if status is deposit refunded or no refund necessary
      if (
        updatedStatus === "deposit refunded" ||
        updatedStatus === "no refund necessary"
      ) {
        docObject.refundedAt = new Date();
        docObject.refundedBy = currentUser.email;
        docObject.refundedByName = currentUser.displayName;
      }

      await setDocData("bookings", data.id, docObject);
      toast.success("Status Updated Successfully");
      setUpdateHappened(false);

      // Send notification to the requestor using dualNotification
      if (data.createdEmail) {
        const notificationObject = {
          recipient: data.createdEmail,
          recipientName: data.createdName,
          sender: currentUser.email,
          senderName: currentUser.displayName,
          subject: "Facility Request Status Update",
          message: `Your facility request has been updated to ${updatedStatus}`,
          type: "facility status update",
          id: data.id,
          url: `/booking/${data.id}`,
          read: false,
        };

        await dualNotification(notificationObject);
      }
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update status");
    } finally {
      setIsStatusUpdating(false);
    }
  };

  const handleUpdatePaymentStatus = async () => {
    try {
      setIsPaymentStatusUpdating(true);
      const updatedPaymentStatus = formData.paymentStatus;
      setNewPaymentStatus(updatedPaymentStatus);

      // Create history entry for payment status update
      const historyEntry = {
        action: `Payment status updated to`,
        at: new Date().toLocaleString(),
        by: currentUser.displayName,
        to: updatedPaymentStatus,
      };

      const docObject = {
        ...formData,
        updatedBy: currentUser.uid,
        updatedName: currentUser.displayName,
        updatedEmail: currentUser.email,
        updateType: "Payment status is updated to " + updatedPaymentStatus,
        history: [...(formData?.history || []), historyEntry],
      };

      // Add paid date if status is paid
      if (updatedPaymentStatus === "paid") {
        docObject.paidAt = new Date();
      }

      await setDocData("bookings", data.id, docObject);
      toast.success("Payment Status Updated Successfully");
      setPaymentUpdateHappened(false);

      // Send notification to the requestor using dualNotification
      if (data.createdEmail) {
        const notificationObject = {
          recipient: data.createdEmail,
          recipientName: data.createdName,
          sender: currentUser.email,
          senderName: currentUser.displayName,
          subject: "Facility Request Payment Status Update",
          message: `Your facility request payment status has been updated to ${updatedPaymentStatus}`,
          type: "facility payment status update",
          id: data.id,
          url: `/booking/${data.id}`,
          read: false,
        };

        await dualNotification(notificationObject);
      }
    } catch (error) {
      console.error("Error updating payment status:", error);
      toast.error("Failed to update payment status");
    } finally {
      setIsPaymentStatusUpdating(false);
    }
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    setFormData(data);
    setAssignedToArray(data.assignedTo || []);
    toast.error("Edit Cancelled");
  };

  const handleAssignment = async () => {
    try {
      setIsAssignmentUpdating(true);
      // Create history entry for assignment
      const historyEntry = {
        action: "Assignment updated",
        at: new Date().toLocaleString(),
        by: currentUser.displayName,
        to: `Assigned to: ${assignedToArray
          .map((item) => item.label)
          .join(", ")}`,
      };

      const docObject = {
        ...formData,
        assignedTo: assignedToArray,
        history: [...(formData?.history || []), historyEntry],
      };

      await setDocData("bookings", data.id, docObject);
      toast.success("Assignment Updated Successfully");

      // Send notification to the requestor
      if (data.createdEmail && data.createdEmail !== currentUser.email) {
        const requestorNotification = {
          recipient: data.createdEmail,
          recipientName: data.createdName,
          sender: currentUser.email,
          senderName: currentUser.displayName,
          subject: "Facility Request Assignment Updated",
          message: `Your facility request has been assigned to: ${assignedToArray
            .map((item) => item.label)
            .join(", ")}`,
          type: "facility assignment",
          id: data.id,
          url: `/booking/${data.id}`,
          read: false,
        };

        await dualNotification(requestorNotification);
      }

      // Send notification to newly assigned users
      for (const user of assignedToArray) {
        if (user.email !== currentUser.email) {
          const assigneeNotification = {
            recipient: user.value,
            recipientName: user.label, // Fallback name if not available
            sender: currentUser.email,
            senderName: currentUser.displayName,
            subject: "New Facility Request Assignment",
            message: `You have been assigned to a facility request`,
            type: "facility assignment",
            id: data.id,
            url: `/facilities?facility=${data.bookingType}&filter=All`,
            read: false,
          };

          await dualNotification(assigneeNotification);
        }
      }
    } catch (error) {
      console.error("Error updating assignment:", error);
      toast.error("Failed to update assignment");
    } finally {
      setIsAssignmentUpdating(false);
    }
  };

  const handleCommentSubmit = async (newComment) => {
    try {
      setIsCommentSubmitting(true);
      // Create history entry for comment
      const historyEntry = {
        action: "Comment added",
        at: new Date().toLocaleString(),
        by: currentUser.displayName,
        to: newComment,
      };

      const docObject = {
        ...formData,
        comments: [
          ...comments,
          {
            message: newComment,
            createdAt: new Date().toLocaleString(),
            createdBy: currentUser.displayName,
            email: currentUser.email,
          },
        ],
        history: [...(formData?.history || []), historyEntry],
      };

      await setDocData("bookings", data.id, docObject);
      toast.success("Comment Added Successfully");

      // Send notification to the requestor using dualNotification
      if (data.createdEmail && data.createdEmail !== currentUser.email) {
        const notificationObject = {
          recipient: data.createdEmail,
          recipientName: data.createdName,
          sender: currentUser.email,
          senderName: currentUser.displayName,
          subject: "New Comment on Facility Request",
          message: `You have a new comment on your facility request`,
          type: "facility comment",
          id: data.id,
          url: `/booking/${data.id}`,
          read: false,
        };

        await dualNotification(notificationObject);
      }
    } catch (error) {
      console.error("Error adding comment:", error);
      toast.error("Failed to add comment");
    } finally {
      setIsCommentSubmitting(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "approved":
        return "bg-green-100 text-green-800 border-green-200";
      case "rejected":
        return "bg-red-100 text-red-800 border-red-200";
      case "pending":
        return "bg-amber-100 text-amber-800 border-amber-200";
      case "completed":
        return "bg-purple-100 text-purple-800 border-purple-200";
      case "deposit refunded":
      case "no refund necessary":
        return "bg-blue-100 text-blue-800 border-blue-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "paid":
        return "bg-green-100 text-green-800 border-green-200";
      case "partial":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "refunded":
        return "bg-purple-100 text-purple-800 border-purple-200";
      case "pending":
        return "bg-amber-100 text-amber-800 border-amber-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  return (
    <div className="min-h-screen p-6 relative text-xs">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between mb-6">
            <div className="title-header">Facility Request Details</div>
            <div className="flex items-center gap-4">
              <button
                className={`px-6 py-3 rounded-lg font-medium transition-all ${
                  editMode
                    ? "bg-red-500 text-white hover:bg-red-600 shadow-sm"
                    : "bg-[#363062] text-white hover:bg-opacity-90 shadow-sm"
                }`}
                onClick={() => {
                  if (editMode) {
                    handleCancelEdit();
                  } else {
                    setEditMode(true);
                  }
                }}
              >
                {editMode ? "Cancel Edit" : "Edit Request"}
              </button>

              <button
                className="p-3 rounded-lg hover:bg-gray-100 transition-colors"
                onClick={() => setViewMOde(false)}
              >
                <svg
                  className="w-6 h-6 text-gray-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Status Badge */}
          <div className="flex items-center gap-4 mb-6">
            <div
              className={`px-4 py-2 rounded-full border ${getStatusColor(
                newStatus
              )} font-medium capitalize`}
            >
              {newStatus}
            </div>
            {editMode && (
              <div className="flex items-center gap-4">
                <SelectInput
                  name={"status"}
                  placeholder={"Select Status"}
                  options={statusOptions}
                  onChange={(selected) => handleSelect(selected)}
                  value={statusOptions.find(
                    (item) => item.value === formData.status
                  )}
                  className="w-48"
                />
                <button
                  className="px-5 py-2 bg-[#363062] text-white rounded-lg hover:bg-opacity-90 disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-sm flex items-center justify-center min-w-[120px]"
                  disabled={!updateHappened || isStatusUpdating}
                  onClick={handleUpdateStatus}
                >
                  {isStatusUpdating ? (
                    <LoadingSpinner text="Updating..." />
                  ) : (
                    "Update Status"
                  )}
                </button>
              </div>
            )}
          </div>

          {/* Payment Status Badge */}
          <div className="flex items-center gap-4 mb-6">
            <div
              className={`px-4 py-2 rounded-full border ${getPaymentStatusColor(
                newPaymentStatus
              )} font-medium capitalize`}
            >
              Payment: {newPaymentStatus}
            </div>
            {editMode && (
              <div className="flex items-center gap-4">
                <SelectInput
                  name={"paymentStatus"}
                  placeholder={"Select Payment Status"}
                  options={paymentStatusOptions}
                  onChange={(selected) => handlePaymentStatusSelect(selected)}
                  value={paymentStatusOptions.find(
                    (item) => item.value === formData.paymentStatus
                  )}
                  className="w-48"
                />
                <button
                  className="px-5 py-2 bg-[#363062] text-white rounded-lg hover:bg-opacity-90 disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-sm flex items-center justify-center min-w-[120px]"
                  disabled={!paymentUpdateHappened || isPaymentStatusUpdating}
                  onClick={handleUpdatePaymentStatus}
                >
                  {isPaymentStatusUpdating ? (
                    <LoadingSpinner text="Updating..." />
                  ) : (
                    "Update Payment"
                  )}
                </button>
              </div>
            )}
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Request Details */}
            <div className="lg:col-span-2 space-y-6">
              {/* Request Information Card */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
                <div className="border-b border-gray-100 p-4">
                  <h3 className="font-semibold text-lg text-[#363062]">
                    Request Information
                  </h3>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <div className="text-sm text-gray-500 mb-1">
                        Booking Type
                      </div>
                      <div className="font-medium">{data?.bookingType}</div>
                    </div>

                    <div>
                      <div className="text-sm text-gray-500 mb-1">
                        {getCapitalizedTerm("identifier")}
                      </div>
                      <div className="font-medium">{data?.identifier}</div>
                    </div>

                    <div>
                      <div className="text-sm text-gray-500 mb-1">Date</div>
                      <div className="font-medium">
                        {data?.date
                          ? new Date(
                              data.date.seconds * 1000
                            ).toLocaleDateString("en-US", {
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                            })
                          : ""}
                      </div>
                    </div>

                    <div>
                      <div className="text-sm text-gray-500 mb-1">Time</div>
                      <div className="font-medium">
                        {data?.startTime} - {data?.endTime}
                      </div>
                    </div>

                    <div>
                      <div className="text-sm text-gray-500 mb-1">
                        Requestor Name
                      </div>
                      <div className="font-medium">{data?.createdName}</div>
                    </div>

                    <div>
                      <div className="text-sm text-gray-500 mb-1">Email</div>
                      <div className="font-medium">{data?.createdEmail}</div>
                    </div>

                    <div>
                      <div className="text-sm text-gray-500 mb-1">
                        Created At
                      </div>
                      <div className="font-medium">
                        {formatDate(data?.createdAt)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Comments Section */}
              <CommentsSection
                comments={comments}
                onCommentSubmit={handleCommentSubmit}
                isSubmitting={isCommentSubmitting}
              />
            </div>

            {/* Right Column - Assignment & History */}
            <div className="space-y-6">
              {/* Assignment Card */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-100">
                <div className="border-b border-gray-100 p-4">
                  <h3 className="font-semibold text-lg text-[#363062]">
                    Task Assignment
                  </h3>
                </div>
                <div className="p-6">
                  <TaskAssignmentModule
                    assignedToArray={assignedToArray}
                    setAssignedToArray={setAssignedToArray}
                    editMode={editMode}
                  />
                  {editMode && (
                    <div className="mt-6 flex items-center gap-4 justify-end">
                      <button
                        className="px-6 py-2.5 bg-[#363062] text-white rounded-lg hover:bg-opacity-90 font-medium shadow-sm flex items-center justify-center min-w-[140px]"
                        onClick={handleAssignment}
                        disabled={isAssignmentUpdating}
                      >
                        {isAssignmentUpdating ? (
                          <LoadingSpinner text="Saving..." />
                        ) : (
                          "Save Assignment"
                        )}
                      </button>
                      <button
                        className="px-6 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium"
                        onClick={handleCancelEdit}
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* History Card */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
                <div className="border-b border-gray-100 p-4">
                  <h3 className="font-semibold text-lg text-[#363062]">
                    History
                  </h3>
                </div>
                <div className="p-6">
                  <HistoryModule data={formData.history || []} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
